import React, { useMemo } from "react";

type Props = {
  data: number[];
  width?: number;
  height?: number;
  labels?: string[];
  showValues?: boolean;
  showGrid?: boolean;
  colorByValue?: boolean;
  /** Show player/bar names below each bar */
  showLabels?: boolean;
  /** Font size for labels below bars (default 18) */
  labelFontSize?: number;
  /** Font size for values above bars (default 20) */
  valueFontSize?: number;
  /** Cap bar width so few bars don't become huge blocks (e.g. 100) */
  maxBarWidth?: number;
  /** Add headroom above max value so bars don't fill full height (e.g. 1.15 = 15% headroom) */
  scaleHeadroom?: number;
};

/**
 * Lightweight SVG BarChart (no external chart libs).
 * Keeps the API compatible with the Next.js dashboard usage.
 */
export default function BarChart({
  data,
  width = 800,
  height = 300,
  labels = [],
  showValues = false,
  showGrid = true,
  colorByValue = false,
  showLabels = true,
  labelFontSize: labelFontSizeProp,
  valueFontSize: valueFontSizeProp,
  maxBarWidth,
  scaleHeadroom = 1,
}: Props) {
  const dataMax = Math.max(1, ...data.map((n) => (Number.isFinite(n) ? n : 0)));
  const max = dataMax * scaleHeadroom;
  const padding = 28;
  const labelFontSize = labelFontSizeProp ?? 18;
  const valueFontSize = valueFontSizeProp ?? 20;
  const labelSpace = showLabels && labels.length > 0 ? Math.max(20, labelFontSize + 8) : 0;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2 - labelSpace;
  const barGap = 8;
  let barW = data.length ? (innerW - barGap * (data.length - 1)) / data.length : innerW;
  if (maxBarWidth != null && maxBarWidth > 0 && barW > maxBarWidth) {
    barW = maxBarWidth;
  }
  const totalBarWidth = data.length ? data.length * barW + (data.length - 1) * barGap : 0;
  const barStartX = padding + (innerW - totalBarWidth) / 2;
  const labelY = padding + innerH + Math.max(10, Math.round(labelFontSize * 0.8));

  const bars = useMemo(() => {
    return data.map((v, i) => {
      const value = Number.isFinite(v) ? v : 0;
      const h = (value / max) * innerH;
      const x = barStartX + i * (barW + barGap);
      const y = padding + (innerH - h);
      const fill = colorByValue ? (value > 0 ? "#10b981" : "#ef4444") : "#3b82f6";
      const title = labels[i] ?? `#${i + 1}`;
      const maxChars = barW > 50 ? 14 : barW > 30 ? 11 : barW > 20 ? 8 : 0;
      const labelText = maxChars > 0 ? (title.length > maxChars ? `${title.slice(0, maxChars)}…` : title) : "";
      return { x, y, h, value, fill, title, labelText };
    });
  }, [barStartX, barW, barGap, data, innerH, labels, max, padding, colorByValue]);

  const gridLines = showGrid ? 4 : 0;
  const grid = Array.from({ length: gridLines + 1 }).map((_, idx) => {
    const y = padding + (innerH / gridLines) * idx;
    return y;
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      role="img"
      aria-label="Bar chart"
    >
      <rect x="0" y="0" width={width} height={height} fill="transparent" />
      {showGrid &&
        grid.map((y, i) => (
          <line
            key={i}
            x1={padding}
            x2={width - padding}
            y1={y}
            y2={y}
            stroke="rgba(148,163,184,0.35)"
            strokeWidth="1"
          />
        ))}

      {bars.map((b, i) => (
        <g key={i}>
          <rect x={b.x} y={b.y} width={Math.max(1, barW)} height={Math.max(1, b.h)} rx="4" fill={b.fill}>
            <title>{b.title}</title>
          </rect>
          {showValues ? (
            <text
              x={b.x + barW / 2}
              y={b.y - 14}
              textAnchor="middle"
              fontSize={valueFontSize}
              fontWeight={600}
              fill="currentColor"
              opacity={0.95}
            >
              {b.value}
            </text>
          ) : null}
          {showLabels && b.labelText ? (
            <text
              x={b.x + barW / 2}
              y={labelY}
              textAnchor="middle"
              fontSize={labelFontSize}
              fill="currentColor"
              opacity={0.9}
              className="fill-muted-foreground"
            >
              {b.labelText}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}


